"use client";

import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export default function SocialProofSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const successStories = [
  {
    id: 'story_sarah_mike',
    names: 'Sarah & Mike',
    location: 'New York, NY',
    story: 'Matched in 3 days, married 18 months later. MeetX understood what we both needed - someone who valued adventure and family equally.',
    image: "https://images.unsplash.com/photo-1637782148210-2e7289caaac5",
    alt: 'Happy couple Sarah and Mike smiling outdoors in New York City with autumn foliage',
    timeline: 'Matched: Jan 2024 | Married: July 2025'
  },
  {
    id: 'story_jessica_david',
    names: 'Jessica & David',
    location: 'San Francisco, CA',
    story: 'After years of disappointing dates, MeetX found someone who actually gets my sense of humor. We had our first video date and talked for 3 hours straight.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b95f0c2e-1764689981715.png",
    alt: 'Couple Jessica and David laughing together at coffee shop in San Francisco',
    timeline: 'Matched: Mar 2025 | Dating: 10 months'
  },
  {
    id: 'story_emily_james',
    names: 'Emily & James',
    location: 'Austin, TX',
    story: 'The AI knew we were both introverts who love hiking. Our first date was a trail walk, and we&apos;ve been inseparable since. No awkward small talk at a bar.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ce4f4fbf-1770331634771.png",
    alt: 'Couple Emily and James hiking together on mountain trail in Austin Texas',
    timeline: 'Matched: June 2025 | Engaged: Dec 2025'
  }];


  const nextStory = () => {
    setCurrentIndex((prev) => (prev + 1) % successStories?.length);
  };

  const prevStory = () => {
    setCurrentIndex((prev) => (prev - 1 + successStories?.length) % successStories?.length);
  };

  return (
    <section
      id="success-stories"
      ref={sectionRef}
      className="py-24 px-6 bg-gradient-to-b from-background to-muted/10">
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2
            className={`text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
            }>
            
            Real <span className="text-primary">Love Stories</span>
          </h2>
          <p
            className={`text-lg text-muted-foreground font-light transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
            }>
            
            Thousands of couples have found their person through MeetX. Here are just a few of their stories.
          </p>
        </div>

        {/* Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <div className="glass-card rounded-3xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Image */}
              <div className="relative h-80 md:h-auto overflow-hidden">
                <AppImage
                  src={successStories?.[currentIndex]?.image}
                  alt={successStories?.[currentIndex]?.alt}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>

              {/* Content */}
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="HeartIcon" variant="solid" size={20} className="text-primary" />
                  <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                    Success Story
                  </span>
                </div>

                <h3 className="text-3xl font-bold text-foreground mb-2">
                  {successStories?.[currentIndex]?.names}
                </h3>

                <p className="text-sm text-muted-foreground mb-6">
                  {successStories?.[currentIndex]?.location}
                </p>

                <blockquote className="text-foreground leading-relaxed mb-6 italic">
                  &quot;{successStories?.[currentIndex]?.story}&quot;
                </blockquote>

                <p className="text-xs text-muted-foreground font-medium">
                  {successStories?.[currentIndex]?.timeline}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prevStory}
              className="w-12 h-12 rounded-full glass-card-hover flex items-center justify-center"
              aria-label="Previous story">
              
              <Icon name="ChevronLeftIcon" size={20} className="text-foreground" />
            </button>

            {/* Dots */}
            <div className="flex gap-2">
              {successStories?.map((_, index) =>
              <button
                key={`dot_${index}`}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex ? 'bg-primary w-8' : 'bg-white/20'}`
                }
                aria-label={`Go to story ${index + 1}`} />

              )}
            </div>

            <button
              onClick={nextStory}
              className="w-12 h-12 rounded-full glass-card-hover flex items-center justify-center"
              aria-label="Next story">
              
              <Icon name="ChevronRightIcon" size={20} className="text-foreground" />
            </button>
          </div>
        </div>
      </div>
    </section>);

}